import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import BookingDto from '@/models/Bookings/BookingDto';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import BookingRoleWeekStatisticsDto from '@/models/BookingRoles/BookingRoleWeekStatisticsDto';
import FilterBookingRoleStatisticsRequest from '@/models/BookingRoles/FilterBookingRoleStatisticsRequest';
import ShiftDto from '@/models/Shifts/ShiftDto';
import FilterBookingRoleStatisticsViewModel from '@/models/BookingRoles/FilterBookingRoleStatisticsViewModel';
import BranchDto from '@/models/Branches/BranchDto';
import DeskDto from '@/models/Desks/DeskDto';
import UserProfileDto from '@/models/Users/UserProfileDto';
import CompanyProfileSearchRequest from '@/models/Companies/CompanyProfileSearchRequest';
import LocationProfileSearchRequest from '@/models/Locations/LocationProfileSearchRequest';
import CompanyProfileDto from '@/models/Companies/CompanyProfileDto';
import LocationProfileDto from '@/models/Locations/LocationProfileDto';
import { filter } from 'vue/types/umd';
import { AgencySettingsModule } from './agencySettings';
import DateTimeUtils from '@/lib/dateTimeUtils';
import CurrentBookingRoleStatisticsSearchCriteria from '@/models/BookingRoles/CurrentBookingRoleStatisticsSearchCriteria';
import GenerateShiftTimesheetPdfRequest from '@/models/Shifts/GenerateShiftTimesheetPdfRequest';

export interface IAgencyDashboardState {
  loading: boolean;
  bookingRoleWeekStatistics: BookingRoleWeekStatisticsDto;
  roleDateShifts: ShiftDto[];
  headers: any[];
  filterBookingRoleStatisticsViewModel: FilterBookingRoleStatisticsViewModel;
  currentStatisticsSearchCriteria: CurrentBookingRoleStatisticsSearchCriteria;
  loadingDialog: boolean;
  
}

@Module({ dynamic: true, store, name: 'AgencyDashboard', namespaced: true})
class AgencyDashboard extends VuexModule implements IAgencyDashboardState {
  
  public loading = true;
  public bookingRoleWeekStatistics: BookingRoleWeekStatisticsDto = new BookingRoleWeekStatisticsDto();
  public roleDateShifts: ShiftDto[] = [];
  public headers: any[] = [];
  public filterBookingRoleStatisticsViewModel: FilterBookingRoleStatisticsViewModel = new FilterBookingRoleStatisticsViewModel();
  public currentStatisticsSearchCriteria: CurrentBookingRoleStatisticsSearchCriteria = new CurrentBookingRoleStatisticsSearchCriteria();
  public loadingDialog = false;

  @Action 
  async mapUserDefaults() {
    const userProfile = AppModule.userProfile;
    this.context.commit("setUserDefaults", userProfile);
  }

  @Action
  async getBookingRoleStatistics() {
    const request = new FilterBookingRoleStatisticsRequest();

    if (this.filterBookingRoleStatisticsViewModel.selectedBranch != null) {
      request.branchId = this.filterBookingRoleStatisticsViewModel.selectedBranch.id;
    }

    if (this.filterBookingRoleStatisticsViewModel.selectedDesk != null) {
      request.deskId = this.filterBookingRoleStatisticsViewModel.selectedDesk.id;
    }

    request.startAtFrom = this.filterBookingRoleStatisticsViewModel.startAtFrom;

    if (this.filterBookingRoleStatisticsViewModel.selectedCompany != null) {
      request.companyId = this.filterBookingRoleStatisticsViewModel.selectedCompany.id;
    }

    if (this.filterBookingRoleStatisticsViewModel.selectedLocation != null) {
      request.locationId = this.filterBookingRoleStatisticsViewModel.selectedLocation.id;
    }

    this.setLoading(true);
    await ajax.post(`/api/v1/agency/summaries/bookings/roles/week`, request)
    .then(response => {
      if (response.data.bookingRoleWeeks === null) {
          response.data.bookingRoleWeeks = [];
      }
      this.context.commit("setBookingRoleStatistics", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoading(false);
    });
  }

  @Action
  async getRoleDateShiftSummary(request: any) {
    this.context.commit("setRoleDateShiftSummary", []);
    this.setLoadingDialog(true);
    await ajax.get(`/api/v1/agency/bookings/${request.bookingId}/dates/${request.bookingDateId}/roles/${request.bookingRoleId}/shifts`)
    .then(response => {
      if (response.data === null) {
          response.data = [];
      }
      this.context.commit("setRoleDateShiftSummary", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async getShiftTimesheetPdf(request: GenerateShiftTimesheetPdfRequest) {
    
    this.setLoadingDialog(true);
    const filename = request.filename;
    request.filename = undefined;

    await ajax.post(`/api/v1/agency/doc-generator`, request, { responseType: 'blob'})
    .then(response => {
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(response.data);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async getCompaniesForSearchOptions() {
    const request = new CompanyProfileSearchRequest();
    //this.setLoading(true);
    await ajax.post(`/api/v1/agency/companies/search`, request)
    .then(response => {
      if (response.data === null) {
        response.data = [];
      }
      this.context.commit("setCompanies", response.data);
      //this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      //this.setLoading(false);
    });
  }

  @Action
  async getLocationsForSearchOptions() {
    const request = new LocationProfileSearchRequest();
    //this.setLoading(true);
    await ajax.post(`/api/v1/agency/locations/search`, request)
    .then(response => {
      if (response.data === null) {
        response.data = [];
      }
      this.context.commit("setLocations", response.data);
      //this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      //this.setLoading(false);
    });
  }

  @Action
  cleanState() {
    this.context.commit("resetState");
  }

  @Mutation
  resetState() {
    this.loading = true;
    this.loadingDialog = false;
    this.filterBookingRoleStatisticsViewModel = new FilterBookingRoleStatisticsViewModel();
  }

  @Mutation 
  private async setUserDefaults(userProfile: UserProfileDto) {
    let branch: BranchDto = null;
    if (userProfile.defaultBranchId !== null) {
      //Loop through each branch until we find the correct one
      branch = AgencySettingsModule.branches.find((findBranch: BranchDto) => findBranch.id === userProfile.defaultBranchId);
      if (branch != undefined) {
        this.filterBookingRoleStatisticsViewModel.selectedBranch = branch;
      }
    }
    if (userProfile.defaultDeskId !== null) {
      //Loop through each desk until we find the correct one
      if (branch !== undefined)
      var desk = branch.desks.find((findDesk: DeskDto) => findDesk.id === userProfile.defaultDeskId);
      if (desk !== undefined) {
        this.filterBookingRoleStatisticsViewModel.selectedDesk = desk
      }
    }
  }

  @Mutation
  private async setLoading(isLoading: boolean) {
      this.loading = isLoading;
  }

  @Mutation
  private async setLoadingDialog(isLoadingDialog: boolean) {
      this.loadingDialog = isLoadingDialog;
  }

  @Mutation
  private async setCompanies(companies: CompanyProfileDto[]) {
      this.filterBookingRoleStatisticsViewModel.companies = companies;
  }

  @Mutation
  private async setLocations(locations: LocationProfileDto[]) {
      this.filterBookingRoleStatisticsViewModel.locations = locations;
  }

  @Mutation
  private async setRoleDateShiftSummary(shifts: ShiftDto[]) {
      this.roleDateShifts = shifts;
  }

  @Mutation
  private async setBookingRoleStatistics(bookingRoleWeekStatistics: BookingRoleWeekStatisticsDto) {
    // Update the latest search criteria 
    this.currentStatisticsSearchCriteria = new CurrentBookingRoleStatisticsSearchCriteria();
    this.currentStatisticsSearchCriteria.startAtFrom = this.filterBookingRoleStatisticsViewModel.startAtFrom;
    this.currentStatisticsSearchCriteria.selectedCompany = this.filterBookingRoleStatisticsViewModel.selectedCompany;
    this.currentStatisticsSearchCriteria.selectedLocation = this.filterBookingRoleStatisticsViewModel.selectedLocation;
    this.currentStatisticsSearchCriteria.selectedBranch = this.filterBookingRoleStatisticsViewModel.selectedBranch;
    this.currentStatisticsSearchCriteria.selectedDesk = this.filterBookingRoleStatisticsViewModel.selectedDesk;

    this.bookingRoleWeekStatistics = bookingRoleWeekStatistics;
    this.headers = [
      { text: 'Desk', value: 'deskName', showGroupBy: true },
      { text: AppModule.currentAgencyContext.labels.location, value: 'locationName' },
      { text: 'Position', value: 'positionName' },
      { text: 'Percentage', value: 'percentage' },
      { text: 'Mon (' + this.bookingRoleWeekStatistics.dates[0] + ")" },
      { text: 'Tue (' + this.bookingRoleWeekStatistics.dates[1] + ")" },
      { text: 'Wed (' + this.bookingRoleWeekStatistics.dates[2] + ")" },
      { text: 'Thu (' + this.bookingRoleWeekStatistics.dates[3] + ")" },
      { text: 'Fri (' + this.bookingRoleWeekStatistics.dates[4] + ")" },
      { text: 'Sat (' + this.bookingRoleWeekStatistics.dates[5] + ")" },
      { text: 'Sun (' + this.bookingRoleWeekStatistics.dates[6] + ")" },
    ]
  }
}

export const AgencyDashboardModule = getModule(AgencyDashboard)
